import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export default function TransportationTable8({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) {
    return <p>No data available</p>;
  }

  /* =============================
     FILTER + SORT DATA
  ============================== */

  const filteredData = data
    .filter((x) => Number(x.emp_count) > 0)
    .sort((a, b) => Number(b.emp_count) - Number(a.emp_count));

  const industries = filteredData.map((d) => d.industry_label);

  const counts = filteredData.map((d) => Number(d.emp_count));

  const totalEmployment = counts.reduce((sum, val) => sum + val, 0);

  const topIndustry = filteredData[0];

  /* =============================
     CHART DATA
  ============================== */

  const chartData = {
    labels: industries,
    datasets: [
      {
        label: "Employment Count",
        data: counts,
        backgroundColor: "#2563eb",
        borderRadius: 6,
        barThickness: 18,
      },
    ],
  };

  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "🏭 Industry Employment (Age 14–18)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return context.parsed.x.toLocaleString("en-US");
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Employment Count",
        },
        ticks: {
          callback: (value) => value.toLocaleString("en-US"),
        },
      },
      y: {
        title: {
          display: true,
          text: "Industry",
        },
      },
    },
  };

  /* =============================
     PDF EXPORT
  ============================== */

  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdfDoc = new jsPDF("l", "mm", "a4");

      const pageWidth = pdfDoc.internal.pageSize.getWidth();
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdfDoc.addImage(utahData, "PNG", 20, 10, 32, 16);
      pdfDoc.setFontSize(22);
      pdfDoc.text("UTahData.org", 60, 20);
      pdfDoc.line(10, 30, pageWidth - 10, 30);

      pdfDoc.addImage(imgData, "PNG", 10, 35, imgWidth, imgHeight);

      pdfDoc.line(10, 190, pageWidth - 10, 190);
      pdfDoc.addImage(utahData, "PNG", 22, 195, 30, 15);
      pdfDoc.setFontSize(10);
      pdfDoc.text("Built by", 60, 205);
      pdfDoc.addImage(bodex, "PNG", 75, 199, 44, 10);
      pdfDoc.text("using", 125, 205);
      pdfDoc.addImage(kpi, "PNG", 140, 195, 20, 20);

      pdfDoc.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        200,
        210,
      );

      pdfDoc.save("industry_employment_14_18.pdf");
    });
  };

  /* =============================
     UI
  ============================== */

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Industry Employment (Age 14–18)
        </h2>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Employment
            </p>
            <p className="text-xl font-bold text-blue-800">
              {totalEmployment.toLocaleString("en-US")}
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Top Industry</p>
            <p className="text-lg font-bold text-green-800">
              {topIndustry.industry_label}
            </p>
          </div>

          <div className="p-4 bg-purple-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Top Industry Employment
            </p>
            <p className="text-xl font-bold text-purple-800">
              {Number(topIndustry.emp_count).toLocaleString("en-US")}
            </p>
          </div>
        </div>

        {/* TABLE + CHART */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* TABLE */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Industry</th>
                  <th className="p-2">Employment Count</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`${
                      idx === 0
                        ? "bg-green-100 font-semibold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2">{row.industry_label}</td>
                    <td className="p-2">
                      {Number(row.emp_count).toLocaleString("en-US")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CHART */}
          <div className="w-full h-[500px] bg-white rounded-lg shadow-md p-4">
            <Bar data={chartData} options={options} />
          </div>
        </div>
      </div>

      {/* DOWNLOAD BUTTON */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
